import { useAppDispatch } from '../../../../../redux/hooks';
import { playGame } from '../../../../../redux/slices/gameParrotSlice';
import { getOccButtons, numToCoords } from '../../gameUtils';
import useConnectFourModalStyles from '../useConnectFourModalStyles';

/**
 * Interface representing the props for the Connect Four Button component.
 *
 * - idx The number of the button to help determine its coordinates on the board.
 * - selfId The user ID of the user whose board this button is a part of.
 * - oppId The user ID of the user whose board this button is not a part of.
 * - gameStatus Array containing information for every turn taken in the game.
 * - challenger Boolean representing whether or not the user viewing the board
 *   is the challenger.
 * - canPlay Boolean representing whether or not the user can take their turn.
 */
interface ConnectFourButtonProps {
  idx: number;
  selfId: string;
  oppId: string;
  gameStatus: (string | number | number[][])[][];
  challenger: boolean | undefined;
  canPlay: boolean;
}

/**
 * Connect Four Button component renders a button, many of which make up a connect four game board.
 *
 * - @param idx The number of the button to help determine its coordinates on the board.
 * - @param selfId The user ID of the user whose board this button is a part of.
 * - @param oppId The user ID of the user whose board this button is not a part of.
 * - @param gameStatus Array containing information for every turn taken in the game.
 * - @param challenger Boolean representing whether or not the user viewing the board
 *   @param is the challenger.
 * - @param canPlay Boolean representing whether or not the user can take their turn.
 */
const ConnectFourButton = ({
  idx,
  selfId,
  oppId,
  gameStatus,
  challenger,
  canPlay,
}: ConnectFourButtonProps) => {
  const { classes } = useConnectFourModalStyles();
  const dispatch = useAppDispatch();

  /**
   * Function to handle the clicking of a tic tac toe button which notifies the opponent
   * of the move that the user has made.
   */
  const handleClick = () => {
    if (
      canPlay &&
      !getOccButtons(selfId, gameStatus, 7).includes(idx) &&
      !getOccButtons(oppId, gameStatus, 7).includes(idx)
    ) {
      const [clickVert, clickHor] = numToCoords(idx, 7);
      let canMoveDown = true;
      let movesDown = 0;
      while (canMoveDown) {
        for (const turn of gameStatus) {
          if (
            clickVert + movesDown === 5 ||
            (turn[1] === clickVert + (movesDown + 1) && turn[2] === clickHor)
          ) {
            canMoveDown = false;
            break;
          }
        }
        if (canMoveDown) movesDown++;
      }

      dispatch(
        playGame({
          going: selfId,
          waiting: oppId,
          data: [clickVert + movesDown, clickHor],
          type: 'turn',
        }),
      );
    }
  };

  /**
   * Function to get the class of this button, if any. The two possible classes to return
   * represent buttons at the top or bottom of their respective columns.
   *
   * @returns the class of this button.
   */
  const getButtonClass = () => {
    if (idx >= 0 && idx < 7) return classes.topButton;
    if (idx >= 35 && idx < 42) return classes.bottomButton;
    return '';
  };

  return (
    <button
      className={`${classes.button} ${getButtonClass()}
        ${getOccButtons(selfId, gameStatus, 7).includes(idx) && challenger && classes.occByChallenger}
        ${getOccButtons(selfId, gameStatus, 7).includes(idx) && !challenger && classes.occByChallenged}
        ${getOccButtons(oppId, gameStatus, 7).includes(idx) && challenger && classes.occByChallenged}
        ${getOccButtons(oppId, gameStatus, 7).includes(idx) && !challenger && classes.occByChallenger}
        ${
          !getOccButtons(selfId, gameStatus, 7).includes(idx) &&
          !getOccButtons(oppId, gameStatus, 7).includes(idx) &&
          classes.unOcc
        }`}
      onClick={handleClick}
    />
  );
};

export default ConnectFourButton;
